import { Injectable, inject, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { ChatMessage, ChatMessageComment } from '../models/communication-api.model';
import { SessionService } from './session.service';

export interface PresenceUpdate {
  userId: string;
  online: boolean;
}

export interface TypingUpdate {
  conversationId: string;
  userId: string;
  typing: boolean;
}

export interface ConversationUpdated {
  conversationId: string;
  lastMessageAt: string;
  lastMessagePreview: string;
  senderId: string;
}

export interface ChatGoalsUpdated {
  conversationId: string;
  studentUserId: string;
  goals: Array<{
    id: string;
    kind: 'lexicon' | 'grammar' | 'subject';
    label: string;
    count?: number;
  }>;
}

export interface MessageCommentEvent {
  conversationId: string;
  comment: ChatMessageComment;
  updated: boolean;
}

type Handler<T> = (value: T) => void;

/**
 * Socket.IO client for the `/communication` namespace.
 * Always authenticates with the session JWT (`auth.token` + query fallback).
 * Connect on login / session restore; disconnect on logout.
 *
 * Events are fanned out to every subscriber so each active session
 * (mobile/web/tab) can apply live chat updates independently.
 */
@Injectable({ providedIn: 'root' })
export class CommunicationSocketService {
  private readonly session = inject(SessionService);
  private socket: Socket | null = null;
  private activeConversationId: string | null = null;

  private readonly messageHandlers = new Set<Handler<ChatMessage>>();
  private readonly presenceHandlers = new Set<Handler<PresenceUpdate>>();
  private readonly typingHandlers = new Set<Handler<TypingUpdate>>();
  private readonly conversationUpdatedHandlers = new Set<Handler<ConversationUpdated>>();
  private readonly goalsUpdatedHandlers = new Set<Handler<ChatGoalsUpdated>>();
  private readonly commentHandlers = new Set<Handler<MessageCommentEvent>>();

  readonly connected = signal(false);
  readonly lastMessage = signal<ChatMessage | null>(null);
  readonly presence = signal<PresenceUpdate | null>(null);
  readonly typing = signal<TypingUpdate | null>(null);
  readonly conversationUpdated = signal<ConversationUpdated | null>(null);
  readonly goalsUpdated = signal<ChatGoalsUpdated | null>(null);
  readonly commentEvent = signal<MessageCommentEvent | null>(null);

  /**
   * Open (or re-open) the chat socket using the given token, or the
   * current session token when omitted.
   */
  connect(token?: string | null): void {
    const authToken = token ?? this.session.token();
    if (!authToken) {
      return;
    }

    if (this.socket?.connected) {
      this.applyAuth(authToken);
      this.rejoinActiveConversation();
      return;
    }

    if (this.socket) {
      this.applyAuth(authToken);
      this.socket.connect();
      return;
    }

    const { url, path } = this.wsConfig();
    this.socket = io(url, {
      path,
      transports: ['websocket', 'polling'],
      // Dynamic auth so reconnects always carry the latest JWT.
      auth: (cb) => {
        const latest = this.session.token() ?? authToken;
        cb({ token: latest });
      },
      query: { token: authToken },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    this.bindSocketEvents();
  }

  /** Drop the socket immediately (logout / 401). */
  disconnect(): void {
    if (!this.socket) {
      this.connected.set(false);
      this.activeConversationId = null;
      return;
    }
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.activeConversationId = null;
    this.connected.set(false);
    this.lastMessage.set(null);
    this.presence.set(null);
    this.typing.set(null);
    this.conversationUpdated.set(null);
    this.goalsUpdated.set(null);
    this.commentEvent.set(null);
  }

  joinConversation(conversationId: string): void {
    this.activeConversationId = conversationId;
    if (!this.ensureConnected()) {
      // Will join once the socket connects / reconnects.
      return;
    }
    this.socket?.emit('conversation:join', { conversationId });
  }

  leaveConversation(): void {
    this.activeConversationId = null;
  }

  onMessage(handler: Handler<ChatMessage>): () => void {
    return this.addHandler(this.messageHandlers, handler);
  }

  onPresence(handler: Handler<PresenceUpdate>): () => void {
    return this.addHandler(this.presenceHandlers, handler);
  }

  onTyping(handler: Handler<TypingUpdate>): () => void {
    return this.addHandler(this.typingHandlers, handler);
  }

  onConversationUpdated(handler: Handler<ConversationUpdated>): () => void {
    return this.addHandler(this.conversationUpdatedHandlers, handler);
  }

  onGoalsUpdated(handler: Handler<ChatGoalsUpdated>): () => void {
    return this.addHandler(this.goalsUpdatedHandlers, handler);
  }

  onComment(handler: Handler<MessageCommentEvent>): () => void {
    return this.addHandler(this.commentHandlers, handler);
  }

  sendMessage(
    conversationId: string,
    body: string,
    clientMessageId?: string,
  ): Promise<{ ok: boolean; chatMessage?: ChatMessage; error?: string }> {
    return new Promise((resolve) => {
      if (!this.ensureConnected()) {
        resolve({ ok: false, error: 'Not connected' });
        return;
      }
      this.socket!.emit(
        'message:send',
        { conversationId, body, clientMessageId },
        (ack: { ok?: boolean; message?: ChatMessage | string }) => {
          if (ack?.ok && ack.message && typeof ack.message === 'object') {
            resolve({ ok: true, chatMessage: ack.message });
            return;
          }
          resolve({
            ok: false,
            error: typeof ack?.message === 'string' ? ack.message : 'Send failed',
          });
        },
      );
    });
  }

  startTyping(conversationId: string): void {
    if (!this.ensureConnected()) return;
    this.socket?.emit('typing:start', { conversationId });
  }

  stopTyping(conversationId: string): void {
    if (!this.ensureConnected()) return;
    this.socket?.emit('typing:stop', { conversationId });
  }

  syncGoals(
    conversationId: string,
    studentUserId: string,
    goals: ChatGoalsUpdated['goals'],
  ): void {
    if (!this.ensureConnected()) return;
    this.socket?.emit('goals:sync', { conversationId, studentUserId, goals });
  }

  private addHandler<T>(set: Set<Handler<T>>, handler: Handler<T>): () => void {
    set.add(handler);
    return () => set.delete(handler);
  }

  private emitToHandlers<T>(set: Set<Handler<T>>, value: T): void {
    for (const handler of set) {
      try {
        handler(value);
      } catch {
        // Keep fan-out alive even if one subscriber throws.
      }
    }
  }

  private ensureConnected(): boolean {
    const token = this.session.token();
    if (!token) {
      this.disconnect();
      return false;
    }
    if (!this.socket?.connected) {
      this.connect(token);
      return false;
    }
    this.applyAuth(token);
    return true;
  }

  private applyAuth(token: string): void {
    if (!this.socket) return;
    this.socket.auth = { token };
    if (this.socket.io?.opts) {
      this.socket.io.opts.query = { ...(this.socket.io.opts.query as object), token };
    }
  }

  private bindSocketEvents(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.connected.set(true);
      this.rejoinActiveConversation();
    });
    this.socket.on('disconnect', () => this.connected.set(false));
    this.socket.on('connect_error', () => this.connected.set(false));

    this.socket.io.on('reconnect_attempt', () => {
      const token = this.session.token();
      if (!token) {
        this.disconnect();
        return;
      }
      this.applyAuth(token);
    });

    this.socket.on('message:new', (message: ChatMessage) => {
      this.lastMessage.set(message);
      this.emitToHandlers(this.messageHandlers, message);
    });
    this.socket.on('presence:update', (update: PresenceUpdate) => {
      this.presence.set(update);
      this.emitToHandlers(this.presenceHandlers, update);
    });
    this.socket.on('typing', (update: TypingUpdate) => {
      this.typing.set(update);
      this.emitToHandlers(this.typingHandlers, update);
    });
    this.socket.on('conversation:updated', (update: ConversationUpdated) => {
      this.conversationUpdated.set(update);
      this.emitToHandlers(this.conversationUpdatedHandlers, update);
    });
    this.socket.on('goals:updated', (update: ChatGoalsUpdated) => {
      this.goalsUpdated.set(update);
      this.emitToHandlers(this.goalsUpdatedHandlers, update);
    });
    this.socket.on(
      'comment:new',
      (payload: { conversationId: string; comment: ChatMessageComment }) => {
        if (!payload?.comment) {
          return;
        }
        const event: MessageCommentEvent = {
          conversationId: payload.conversationId,
          comment: payload.comment,
          updated: false,
        };
        this.commentEvent.set(event);
        this.emitToHandlers(this.commentHandlers, event);
      },
    );
    this.socket.on(
      'comment:updated',
      (payload: { conversationId: string; comment: ChatMessageComment }) => {
        if (!payload?.comment) {
          return;
        }
        const event: MessageCommentEvent = {
          conversationId: payload.conversationId,
          comment: payload.comment,
          updated: true,
        };
        this.commentEvent.set(event);
        this.emitToHandlers(this.commentHandlers, event);
      },
    );
  }

  private rejoinActiveConversation(): void {
    const conversationId = this.activeConversationId;
    if (!conversationId || !this.socket?.connected) {
      return;
    }
    this.socket.emit('conversation:join', { conversationId });
  }

  /** Socket.IO namespace URL + engine path. Always BASE_URL/ws → /ws/socket.io. */
  private wsConfig(): { url: string; path: string } {
    const fromWsUrl = (rawInput: string): { url: string; path: string } | null => {
      const raw = rawInput.replace(/\/$/, '');
      try {
        const parsed = new URL(raw);
        const prefix = parsed.pathname.replace(/\/$/, '') || '/ws';
        return {
          url: `${parsed.origin}/communication`,
          path: `${prefix}/socket.io`,
        };
      } catch {
        return null;
      }
    };

    if (environment.wsUrl) {
      return (
        fromWsUrl(environment.wsUrl) ?? {
          url: `${environment.wsUrl.replace(/\/$/, '')}/communication`,
          path: '/ws/socket.io',
        }
      );
    }

    const api = environment.apiUrl.replace(/\/api\/v1\/?$/, '');
    if (api.startsWith('http')) {
      return fromWsUrl(`${api}/ws`) ?? {
        url: `${api}/communication`,
        path: '/ws/socket.io',
      };
    }
    if (typeof window !== 'undefined') {
      return {
        url: `${window.location.origin}/communication`,
        path: '/ws/socket.io',
      };
    }
    return { url: '/communication', path: '/ws/socket.io' };
  }
}
